import { Button } from "@result/rui/components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useDispatch } from "react-redux";

import * as LTE from "#/def/lookup-tables.enums.gen";
import useAuthorization from "#/hooks/useAuthorization";
import useCallbackOnMatch from "#/hooks/useCallbackOnMatch";
import apiInstance from "#/redux/api/apiInstance";
import {
  fetchAvailableAuthorizations,
  setAuthorization,
} from "#/redux/resource/CurrentAuthorizationSlice";
import * as session from "#/utils/SessionStorageUtils";
import { getAuthIdFromCookie, logout, setAuthIdCookie } from "#/utils/auth";
import * as scope from "#/utils/SpeciesScope";
import SPECIES from "#/model/SpeciesCodeList";
import { parse, serialize } from "cookie";

/**
 * Authorization component to handle user authorization
 * selection and management.
 *
 * @param {*} param0
 * @returns
 */
export default function Authorization({ children }) {
  const dispatch = useDispatch();

  const {
    PRESET_AUTHORIZATION_URL_PARAM,
    setOrRedirect,
    set,
    current,
    isLoading,
    available,
    levels,
  } = useAuthorization();
  const [error, setError] = useState(false);

  // When the authorization changes,
  // we need to update the local storage and the API client
  // with the new authorization ID.
  useCallbackOnMatch(({ payload: id }) => {
    session.set(session.keys.AUTHORIZATION, id);
    apiInstance.setAuthorizationId(id);
  }, setAuthorization);

  // When fetching available authorizations fails,
  // we need to set the error state to display the error message.
  useCallbackOnMatch(
    ({ error: { code, message: $message } = {}, payload, ...x }) => {
      const message = !$message || $message === "Rejected" ? payload : $message;
      return setError({ code, message });
    },
    fetchAvailableAuthorizations.rejected
  );

  // When the component mounts, we need to load the
  // current authorization ID from local storage
  useEffect(() => {
    // if authorization is not selected and there is "authorization" in the
    // url query params, we will use that one (this is used for cross-app redirection)
    const urlParams = new URLSearchParams(window.location.search);

    const id =
      urlParams.get(PRESET_AUTHORIZATION_URL_PARAM) ||
      session.get(session.keys.AUTHORIZATION);
    set(id);
    if (id) {
      setAuthIdCookie(id);
    }

    // remove the "authorization" query param from the URL to prevent confusion
    if (urlParams.has(PRESET_AUTHORIZATION_URL_PARAM)) {
      urlParams.delete(PRESET_AUTHORIZATION_URL_PARAM);
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : "") +
        window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // /dev mode only
  useEffect(() => {
    dispatch(fetchAvailableAuthorizations());
  }, [dispatch]);

  switch (true) {
    case !!error:
      // network error while fetching authorizations
      // prevent further rendering
      return (
        <Wrapper>
          <Error {...error} />
        </Wrapper>
      );
    case isLoading === true:
      // still loading available authorizations
      // prevent further rendering
      //
      // @todo user will likely to get stuck here in case of a network error
      //       we should handle that case properly
      return (
        <Wrapper>
          <Loading />
        </Wrapper>
      );
    case available.length === 0:
      // no available authorizations
      // prevent further rendering
      return (
        <Wrapper>
          <NoneAvailable />
        </Wrapper>
      );

    case !current:
      const authorizationCookie = getAuthIdFromCookie();

      let auth = null;
      if (authorizationCookie != null) {
        auth = available.find((value) => value.id === authorizationCookie);
      }

      if (auth != null) {
        setOrRedirect(auth);
      }

      // no authorization selected
      // render available authorizations for selection
      return (
        <Wrapper>
          <Available
            list={available}
            levels={levels}
            onChange={(auth) => {
              setOrRedirect(auth);
            }}
          />
        </Wrapper>
      );

    default:
    // defaulting for eslint
  }

  // we have a valid current authorization
  return children;
}

/**
 * Render available authorizations for selection.
 *
 * This is a presentational component.
 * It expects a list of authorizations (formatted and ready) and an onChange callback.
 *
 * @param {Array} list - List of available authorizations
 * @param {Array} levels - Lookup table levels for formatting (translations)
 * @param {Function} onChange - Callback when an authorization is selected
 * @returns JSX.Element
 */
function Available({ list, levels, onChange }) {
  const { t } = useTranslation();
  const choices = [];

  const { formatter } = useAuthorization();

  for (const auth of list) {
    choices.push(
      <div
        key={auth.id}
        id={auth.id}
        onClick={() => onChange(auth)}
        className="button is-block authorization-choice"
        style={{ cursor: "pointer", margin: "0.5em 0" }}>
        {formatter(auth, levels)}
      </div>
    );
  }

  return (
    <>
      <div className="mb-5">{t("authorization.selectRole")}</div>
      <div>{choices}</div>
    </>
  );
}

/**
 * Render loading message while authorizations are being fetched.
 *
 * @returns JSX.Element
 */
function Loading() {
  // @todo i18n
  return (
    <div className="is-flex is-justify-content-center is-align-items-center">
      <span className="loader"></span>
    </div>
  );
}

/**
 * Render message when no authorizations are available.
 *
 * @returns JSX.Element
 */
function Error({ message }) {
  const { t } = useTranslation();

  return (
    <>
      <div>{message}</div>
      <div className="my-3">{t("authorization.error")}</div>
    </>
  );
}

/**
 * Render message when no authorizations are available.
 *
 * @returns JSX.Element
 */
function NoneAvailable() {
  const { t } = useTranslation();

  return <div>{t("authorization.noneAvailable")}</div>;
}

/**
 * Wrapper component to style the authorization selection.
 *
 * @returns JSX.Element
 */
function Wrapper({ children }) {
  const { t } = useTranslation();

  const auth = useAuth();

  const handleLogout = async () => {
    await logout({ auth });
  };

  return (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">{t("admin.authorizations.title")}</p>
        </header>
        <section className="modal-card-body">{children}</section>
        <footer className="modal-card-foot is-flex">
          <Button
            label={t("logout")}
            onClick={handleLogout}
            color="primary"
            className="ml-auto"
          />
        </footer>
      </div>
    </div>
  );
}

/**
 * Render children if the user is authorized for the specified role.
 *
 * @param {string|array} param.as role to check against
 * @param {boolean} [param.invert=false] if true, render children if user is NOT authorized for the specified role(s)
 * @param {JSX.Element|function} param.children content to render if authorized
 * @param {JSX.Element|null} [param.fallback=null] content to render if not authorized (optional)
 * @returns {JSX.Element|null}
 */
export function IsAuthorized({
  as,
  invert = false,
  children,
  fallback = null,
}) {
  const { level, full } = useAuthorization();

  if ((Array.isArray(as) ? as : [as]).includes(level) !== invert) {
    if (typeof children === "function") {
      // @todo pass in authorization info to
      // children function if needed
      const params = { authorization: full };
      return children(params);
    }

    // children is not a function, render as is
    return children;
  }

  return fallback;
}

/**
 * Shortcut for checking if user is authorized as VF
 */
export function IsVF(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.VF} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as BF
 */
export function IsBF(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.BF} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as admin
 */
export function IsAdmin(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.ADMIN} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as selector
 */
export function IsSelector(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.SELEKCTIONIST} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as viewer
 */
export function IsControlor(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.KONTROLOR} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as breeder
 */
export function IsBreeder(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.REJEC} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as veterinarian
 */
export function IsVeterinarian(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.VETERINAR} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as kopitarna lipica
 */
export function IsKopitarnaLipica(props) {
  return <IsAuthorized as={LTE.SifNivoDostopa.KL} {...props} />;
}

/**
 * Shortcut for checking if user is authorized as KL (Kobilarna Lipica)
 */
export function IsKL(props) {
  return <IsAuthorized as="KL" {...props} />;
}
